// ConfigActions — the contractual configuration's ONE registry and its overlays
// (ADR-036 §7 — the four kinds; mirrors the exemplar `pages/teams/TeamActions.tsx`).
//
//   • `configActs` is the PURE registry — rows with a kind and a `show` predicate
//     over the status; the shape test (`components/patterns/actionGrammar.test.ts`)
//     walks it.
//   • `useConfigActions` resolves it for a configuration and returns the ACTS the
//     peek composes from and the OVERLAYS (the delete confirm, mounted ONCE beside
//     the peek).
//
// A configuration is PEEK-ONLY (its form is a page of its own, never a detail
// page): the top row holds its one edit — Edit configuration, the form (a route;
// section-less — every card's gap is fixed by the ONE form, and the body's
// observation cards say what is missing); the door holds Clone as variant (a
// route — the form seeded from this one), Disable / Reactivate (direct) and Delete
// (the danger act — confirmed; the server refuses a used one). The gate is
// `contractual_config:manage`; the peek itself is member-readable.

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContractualConfig } from '@ems-app/schemas/invoicing/responses';
import { resolveActs, type ActionItem, type EntityAct } from '../../components/patterns/entityRegistry.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { icons } from '../../components/ui/icons.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { useDeleteConfig, useSetConfigDisabled } from '../../hooks/useContractCatalogue.js';
import { useQuickSave } from '../../components/patterns/forms/useQuickSave.js';

export type ConfigStatus = 'active' | 'disabled';

/** What each act does — the hook wires these to its overlays. */
export interface ConfigActHandlers {
  disable: () => void;
  reactivate: () => void;
  remove: () => void;
}

/** The closed-over facts the rows read — the routes the form opens at. */
export interface ConfigActFacts {
  editRoute: string;
  cloneRoute: string;
}

/** The configuration's registry — pure: the rows, their kinds and predicates. */
export function configActs(h: ConfigActHandlers, facts: ConfigActFacts): ActionItem<ConfigStatus>[] {
  return [
    // The one edit — the form (section-less: the top row's plain button).
    { id: 'edit', kind: 'edit', labelKey: 'form.editTitle', icon: icons.edit, run: { route: facts.editRoute }, gate: 'contractual_config:manage', order: 10 },
    // The door — the operation, then the lifecycle; Delete the danger act, fenced last.
    { id: 'clone', kind: 'operation', labelKey: 'form.cloneTitle', icon: icons.copy, run: { route: facts.cloneRoute }, gate: 'contractual_config:manage', order: 10 },
    { id: 'disable', kind: 'lifecycle', labelKey: 'configs.peek.disable', icon: icons.statusPaused, run: { handler: h.disable }, gate: 'contractual_config:manage', order: 10, show: (ctx) => ctx.status === 'active' },
    { id: 'reactivate', kind: 'lifecycle', labelKey: 'configs.peek.reactivate', icon: icons.restore, run: { handler: h.reactivate }, gate: 'contractual_config:manage', order: 10, show: (ctx) => ctx.status === 'disabled' },
    { id: 'delete', kind: 'lifecycle', labelKey: 'configs.peek.delete', icon: icons.delete, run: { handler: h.remove }, gate: 'contractual_config:manage', order: 20, tone: 'danger' },
  ];
}

export interface UseConfigActionsOptions {
  /** After a delete (the peek closes). */
  onDeleted?: () => void;
}

export interface ConfigActions {
  /** The resolved set — what the peek composes from. */
  acts: EntityAct[];
  /** Every dialog, mounted ONCE beside the peek. */
  overlays: ReactNode;
}

/** The configuration's acts — nothing until it is there. */
export function useConfigActions(projectId: string, cfg: ContractualConfig | undefined, { onDeleted }: UseConfigActionsOptions = {}): ConfigActions {
  const { t } = useTranslation('invoicing');
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  const setDisabled = useSetConfigDisabled(projectId);
  // Disable and Reactivate are low-stakes and direct (no dialog) — a quick save: a refusal said once, the page re-read.
  const quickSwitch = useQuickSave(setDisabled, { key: 'config-switch' });
  const del = useDeleteConfig(projectId);
  // The open confirm, held as the configuration it was opened for (the peek's hook outlives a peek→peek switch).
  const [deleting, setDeleting] = useState<number | null>(null);

  if (!cfg) return { acts: [], overlays: null };
  const toggle = (disabled: boolean) => () => void quickSwitch.save({ configId: cfg.id, disabled });

  const acts = resolveActs(
    configActs(
      { disable: toggle(true), reactivate: toggle(false), remove: () => setDeleting(cfg.id) },
      { editRoute: `/p/${projectId}/configurations/${cfg.id}/edit`, cloneRoute: `/p/${projectId}/configurations/new?from=${cfg.id}` },
    ),
    { can, entitlements, status: cfg.is_disabled ? 'disabled' : 'active' },
    (key) => t(key),
  );

  const overlays = (
    <ConfirmDialog
      open={deleting === cfg.id}
      onClose={() => setDeleting(null)}
      title={t('configs.peek.deleteConfirmTitle', { name: cfg.name })}
      description={t('configs.peek.deleteConfirmBody')}
      confirmLabel={t('configs.peek.delete')}
      onConfirm={async () => {
        await del.mutateAsync(cfg.id);
        onDeleted?.();
      }}
    />
  );

  return { acts, overlays };
}
